import React, { useState, useEffect } from 'react'
import {
  Box,
  Grid,
  Typography,
  Link,
  Button,
  Card,
  CardContent,
  CardMedia,
  CircularProgress,
  Alert,
  Modal,
  TextField,
  InputAdornment,
  IconButton,
  Fade,
  Backdrop,
  Divider,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff, Close } from '@mui/icons-material';
import imgUrl from "../../assets/images/imgUrl";
import axios from 'axios';
import { apiServices } from '../../services/apiServices';
import Swal from 'sweetalert2';

const Gifts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState({}); // Track current image index for each product
  const [isHovered, setIsHovered] = useState({}); // Track hover state for each product

  // Auto slideshow effect for products with multiple images
  useEffect(() => {
    const intervals = {};

    products.forEach((product) => {
      const productImages = product.images?.edges?.map(edge => edge.node) || [];
      const hasMultipleImages = productImages.length > 1;

      if (hasMultipleImages) {
        intervals[product.id] = setInterval(() => {
          // Only advance if not hovered
          if (!isHovered[product.id]) {
            setCurrentImageIndex(prev => {
              const currentIdx = prev[product.id] || 0;
              const nextIdx = (currentIdx + 1) % productImages.length;
              return { ...prev, [product.id]: nextIdx };
            });
          }
        }, 3000); // Change image every 3 seconds
      }
    });

    return () => {
      Object.values(intervals).forEach(interval => clearInterval(interval));
    };
  }, [products, isHovered]);

  // Login modal states
  const [openLoginOptionsModal, setOpenLoginOptionsModal] = useState(false);
  const [selectedProductUrl, setSelectedProductUrl] = useState(null);
  const [selectedLoginType, setSelectedLoginType] = useState('peacekeeper'); // 'peacekeeper' or 'referral'

  // Login form states
  const [isLoading, setIsLoading] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [peacekeeperId, setPeacekeeperId] = useState('');
  const [peacekeeperPassword, setPeacekeeperPassword] = useState('');
  const [showPeacekeeperPassword, setShowPeacekeeperPassword] = useState(false);
  // Login method states
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [showOtpField, setShowOtpField] = useState(false);

  // OTP states
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Fetch products from Shopify GraphQL API
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await axios.post(
          'https://shop.i-am-peacekeeper.com/api/2024-01/graphql.json',
          {
            query: `{
              products(first: 100) {
                edges {
                  node {
                    id
                    title
                    handle
                    description
                    images(first: 10) {
                      edges {
                        node {
                          url
                          altText
                          width
                          height
                        }
                      }
                    }
                  }
                }
              }
            }`
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': '7c22456c425294cc474272ed0be13239'
            }
          }
        );

        if (response.data && response.data.data && response.data.data.products) {
          const productsList = response.data.data.products.edges.map(edge => edge.node);
          setProducts(productsList);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError(err.message || 'Failed to load products. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  // Resend OTP timer
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((timer) => timer - 1);
      }, 1000);
    } else if (resendTimer === 0 && interval) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const startResendTimer = () => {
    setResendTimer(60); // 60 seconds
  };

  const handleSendOtp = async () => {
    if (!peacekeeperId.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter your email address',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    if (!/\S+@\S+\.\S+/.test(peacekeeperId.trim())) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter a valid email address',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    try {
      setOtpLoading(true);
      const response = await apiServices.postAPI('send-login-token', { email: peacekeeperId.trim() });

      if (response.data) {
        setOtpSent(true);
        startResendTimer();
        Swal.fire({
          title: 'OTP Sent',
          text: 'OTP has been sent to your email address',
          icon: 'success',
          showConfirmButton: false,
          timer: 2000,
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          zIndex: 1500,
        });
      } else {
        throw new Error(response.data?.message || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('Error sending OTP:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Failed to send OTP. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendTimer > 0) return;
    await handleSendOtp();
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter the OTP',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    if (otp.length < 4) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter a valid OTP',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    try {
      setOtpLoading(true);
      const response = await apiServices.postAPI('validate-login-token', {
        email: peacekeeperId.trim(),
        otp: otp.trim()
      });

      if (response.data && response.data.success) {
        // After OTP verification, call shopify-login to get redirect URL
        // OTP login validates the user, so we can proceed with Shopify login
        try {
          const shopifyLoginResponse = await apiServices.postAPI('shopify-login', {
            email: peacekeeperId.trim(),
            password: '' // OTP login doesn't require password
          });

          if (shopifyLoginResponse.data && shopifyLoginResponse.data.success) {
            setOpenLoginOptionsModal(false);

            Swal.fire({
              title: 'Success',
              text: 'Login successful!',
              icon: 'success',
              showConfirmButton: false,
              timer: 1500,
              customClass: {
                popup: 'minimal-popup',
                confirmButton: 'minimal-button',
              },
              backdrop: false,
              zIndex: 1500,
            });

            // Redirect to product URL with login parameters
            if (selectedProductUrl) {
              localStorage.removeItem('giftProductUrl');
              if (shopifyLoginResponse.data.redirectUrl) {
                const temp = shopifyLoginResponse.data.redirectUrl.split('?')[1];
                window.open(selectedProductUrl + '?' + temp, '_self');
              } else {
                window.open(selectedProductUrl, '_self');
              }
            } else if (shopifyLoginResponse.data.redirectUrl) {
              window.open(shopifyLoginResponse.data.redirectUrl, '_self');
            } else {
              window.open('https://shop.i-am-peacekeeper.com/', '_self');
            }
          } else {
            throw new Error(shopifyLoginResponse.data?.message || 'Shopify login failed');
          }
        } catch (shopifyError) {
          // If shopify-login fails, still redirect to product (OTP was valid)
          setOpenLoginOptionsModal(false);

          Swal.fire({
            title: 'Success',
            text: 'OTP verified successfully!',
            icon: 'success',
            showConfirmButton: false,
            timer: 1500,
            customClass: {
              popup: 'minimal-popup',
              confirmButton: 'minimal-button',
            },
            backdrop: false,
            zIndex: 1500,
          });

          if (selectedProductUrl) {
            localStorage.removeItem('giftProductUrl');
            window.open(selectedProductUrl, '_self');
          } else {
            window.open('https://shop.i-am-peacekeeper.com/', '_self');
          }
        }
      } else {
        throw new Error(response.data?.message || 'Invalid OTP');
      }
    } catch (error) {
      console.error('Error verifying OTP:', error);
      Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || error.message || 'Invalid OTP. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
    } finally {
      setOtpLoading(false);
    }
  };

  const handleProductClick = (handle) => {
    const productUrl = `https://shop.i-am-peacekeeper.com/products/${handle}`;
    // Store the product URL
    setSelectedProductUrl(productUrl);
    localStorage.setItem('giftProductUrl', productUrl);
    // Open login options modal
    setOpenLoginOptionsModal(true);
  };

  const handleReferralLogin = async (e) => {
    e.preventDefault();

    if (!referralCode.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter your referral code',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    try {
      setIsLoading(true);

      const response = await apiServices.getAPI(`user/check-referral/${referralCode.trim()}`);

      if (response.data && response.data.success) {
        const referralData = response.data.data;

        setOpenLoginOptionsModal(false);

        Swal.fire({
          title: 'Success',
          text: response.data.message || 'Referral verified successfully!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          zIndex: 1500,
        });

        // Redirect to product URL with referral parameters
        if (selectedProductUrl) {
          localStorage.removeItem('giftProductUrl');
          if (referralData.shopify_Referral_ShoppingURL) {
            const temp = referralData.shopify_Referral_ShoppingURL.split('?')[1];
            window.open(selectedProductUrl + '?' + temp, '_self');
          } else {
            window.open(selectedProductUrl, '_self');
          }
        } else if (referralData.shopify_Referral_ShoppingURL) {
          window.open(referralData.shopify_Referral_ShoppingURL, '_self');
        } else {
          window.open('https://shop.i-am-peacekeeper.com/', '_self');
        }
      } else {
        throw new Error(response.data?.message || 'Invalid referral code');
      }
    } catch (error) {
      console.error('Login with Referral ID error:', error);
      Swal.fire({
        title: 'Login Failed',
        text: error.response?.data?.message || error.message || 'Invalid referral code. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePeacekeeperLogin = async (e) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!peacekeeperId.trim() || !peacekeeperPassword.trim()) {
      Swal.fire({
        title: 'Error',
        text: 'Please enter both email and password',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
      return;
    }

    try {
      setIsLoading(true);

      const userEmail = peacekeeperId.trim();

      const shopifyLoginResponse = await apiServices.postAPI('shopify-login', {
        email: userEmail,
        password: peacekeeperPassword
      });

      if (shopifyLoginResponse.data && shopifyLoginResponse.data.success) {
        setOpenLoginOptionsModal(false);

        Swal.fire({
          title: 'Success',
          text: 'Login successful!',
          icon: 'success',
          showConfirmButton: false,
          timer: 1500,
          customClass: {
            popup: 'minimal-popup',
            confirmButton: 'minimal-button',
          },
          backdrop: false,
          zIndex: 1500,
        });

        // Redirect to product URL with login parameters
        if (selectedProductUrl) {
          localStorage.removeItem('giftProductUrl');
          if (shopifyLoginResponse.data.redirectUrl) {
            const temp = shopifyLoginResponse.data.redirectUrl.split('?')[1];
            window.open(selectedProductUrl + '?' + temp, '_self');
          } else {
            window.open(selectedProductUrl, '_self');
          }
        } else if (shopifyLoginResponse.data.redirectUrl) {
          window.open(shopifyLoginResponse.data.redirectUrl, '_self');
        } else {
          navigate('/dashboard');
        }
      } else {
        throw new Error(shopifyLoginResponse.data?.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      Swal.fire({
        title: 'Login Failed',
        text: error.response?.data?.message || 'Invalid email or password. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        customClass: {
          popup: 'minimal-popup',
          confirmButton: 'minimal-button',
        },
        backdrop: false,
        allowOutsideClick: true,
        buttonsStyling: false,
        zIndex: 1500,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExternalLink = () => {
    setOpenLoginOptionsModal(false);
    // Redirect to product URL if available, otherwise redirect to shop homepage
    if (selectedProductUrl) {
      window.open(selectedProductUrl, '_self');
    } else {
      window.open('https://shop.i-am-peacekeeper.com/', '_self');
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '80%', sm: '85%', md: '800px' },
    maxWidth: { xs: '400px', sm: '90%', md: '900px' },
    bgcolor: 'background.paper',
    borderRadius: { xs: 1, sm: 2 },
    boxShadow: 24,
    outline: 'none',
    maxHeight: { xs: '90vh', sm: '90vh' },
    overflow: 'auto',
    p: { xs: 1.25, sm: 2.5, md: 4 },
    mx: { xs: 'auto', sm: 0 },
  };

  // Add style to ensure SweetAlert appears above Modal
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .swal2-container {
        z-index: 1500 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden' }}>
      {/* Gift Hero Background Section */}

      <Box
        className="programGiftSectionBg commonBannerBg"
        sx={{
          backgroundSize: "cover",
          backgroundPosition: "center",
          //   position: "relative",
          color: "white",
          px: 2,
        }}
        mb={5}
      >
        {/* Mini Header (Top-Left) */}
        <Box
          sx={{
            position: "absolute",
            top: 80,
            left: 0,
            display: "flex",
            alignItems: "center",
          }}
          className="doveImg"
        >
          <Box
            component="img"
            src={imgUrl.doveImg}
            alt="Peace Dove Logo"
            sx={{ maxWidth: 200, height: "auto", mr: 1 }}
          />
        </Box>
      </Box>

      {/* Gift Main Section */}
      <Box className="giftProgramSubSection">
        <Grid container direction="column" alignItems="center" spacing={2}>
          <Grid

            size={12}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <img src={imgUrl.peaceIcon} className="w-100 peaceImg" alt="Peace Icon"></img>
            <Typography
              variant="h1"
              className="founder-title mainHeading green-title"
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.35rem', md: '1.75rem' },
                textAlign: 'center',
                px: { xs: 2, sm: 0 },
                mt: { xs: 1, sm: 2 }
              }}
            >
              <i> I Am Peacekeeper </i>  Gifts
            </Typography>

            {/* <Typography variant="h5" className="comingSoongifts mainHeading green-title">
            Coming Soon
            </Typography> */}
          </Grid>
        </Grid>
        {/* <Box  display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{ px: { xs: 2, sm: 3 }, mt: 3 }}
        >
            <img
              src={imgUrl.peaceKeeperGifts}
              className="w-100 peacekeeperInfographic"
              alt="I Am Peacekeeper Gifts"
            ></img>
        </Box> */}

        {/* Visual separator between hero infographic and products */}
        <Box sx={{ maxWidth: 900, mx: 'auto', mt: { xs: 3, sm: 4, md: 5 }, px: { xs: 2, sm: 3 } }}>
          <Divider
            sx={{
              '&::before, &::after': {
                borderColor: '#d7d7d8',
              },
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: { xs: 2, sm: 3 }
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: 'var(--secondary-font)',
                  letterSpacing: { xs: 1, sm: 2 },
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.75rem', sm: '0.9rem', md: '1.1rem' },
                  color: '#214F7D',
                  position: 'relative',
                  zIndex: 2
                }}
              >
                Peacekeeper Gift Collection
              </Typography>
            </Box>
          </Divider>
        </Box>

      </Box>

      {/* Products Section */}
      <Box sx={{ py: { xs: 3, sm: 4, md: 6 }, px: { xs: 1, sm: 2 }, pr: { xs: 2, sm: 3, md: 4 }, width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
            <CircularProgress sx={{ color: '#3A8139' }} />
          </Box>
        )}

        {error && (
          <Box sx={{ maxWidth: '600px', mx: 'auto', mb: 4 }}>
            <Alert severity="error">{error}</Alert>
          </Box>
        )}

        {!loading && !error && products.length > 0 && (
          <Grid
            container
            spacing={{ xs: 2, sm: 2, md: 2, lg: 2, xl: 2 }}
            sx={{
              mx: 'auto',
              px: { xs: 1, sm: 2, md: 2, lg: 2 },
              pr: { xs: 2, sm: 3, md: 4, lg: 4 },
              justifyContent: { xs: 'center', sm: 'flex-start', md: 'flex-start' },
              alignItems: 'stretch',
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box'
            }}
          >
            {products.map((product) => {
              const productImages = product.images?.edges?.map(edge => edge.node) || [];
              const hasMultipleImages = productImages.length > 1;
              const currentIndex = currentImageIndex[product.id] || 0;
              const currentImage = productImages[currentIndex];
              const description = product.description
                ? product.description
                : 'No description available';

              return (
                <Grid
                  size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 4 }}
                  key={product.id}
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'center', sm: 'flex-start', md: 'flex-start' },
                    boxSizing: 'border-box'
                  }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: { xs: 'none', sm: 'translateY(-8px)' },
                        boxShadow: { xs: 2, sm: 6 },
                      },
                      borderRadius: 2,
                      overflow: 'hidden',
                      width: '100%',
                      mx: { xs: 'auto', sm: 0, md: 0 },
                      boxSizing: 'border-box'
                    }}
                  >
                    {currentImage && (
                      <Box
                        onMouseEnter={() => setIsHovered(prev => ({ ...prev, [product.id]: true }))}
                        onMouseLeave={() => setIsHovered(prev => ({ ...prev, [product.id]: false }))}
                        sx={{
                          position: 'relative',
                          width: '100%',
                          height: { xs: '180px', sm: '200px', md: '220px' },
                          backgroundColor: '#f5f5f5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          overflow: 'hidden'
                        }}
                      >
                        {productImages.map((image, index) => (
                          <CardMedia
                            key={index}
                            component="img"
                            sx={{
                              position: 'absolute',
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              padding: { xs: '8px', sm: '10px' },
                              opacity: index === currentIndex ? 1 : 0,
                              transition: 'opacity 0.5s ease-in-out',
                              zIndex: index === currentIndex ? 1 : 0
                            }}
                            image={image.url}
                            alt={image.altText || `${product.title} - Image ${index + 1}`}
                          />
                        ))}
                        
                        {hasMultipleImages && (
                          <Box
                            sx={{
                              position: 'absolute',
                              bottom: { xs: 6, sm: 8 },
                              left: '50%',
                              transform: 'translateX(-50%)',
                              display: 'flex',
                              gap: 0.5,
                              zIndex: 2,
                              backgroundColor: 'rgba(0, 0, 0, 0.3)',
                              padding: { xs: '4px 6px', sm: '6px 8px' },
                              borderRadius: '20px'
                            }}
                          >
                            {productImages.map((_, index) => (
                              <Box
                                key={index}
                                sx={{
                                  width: { xs: 6, sm: 8 },
                                  height: { xs: 6, sm: 8 },
                                  borderRadius: '50%',
                                  backgroundColor: index === currentIndex 
                                    ? '#fff' 
                                    : 'rgba(255, 255, 255, 0.5)',
                                  transition: 'all 0.3s ease',
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}

                    <CardContent sx={{ p: { xs: 1.25, sm: 1.5 }, pt: 0, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography
                        variant="subtitle1"
                        component="h3"
                        gutterBottom
                        sx={{
                          fontWeight: 600,
                          color: '#214F7D',
                          mb: 0.5,
                          fontSize: { xs: '0.9rem', sm: '0.95rem' },
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: { xs: '44px', sm: '48px' }
                        }}
                      >
                        {product.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.8rem' },
                          // display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          flexGrow: 1
                        }}
                      >
                        {description}
                      </Typography>
                    </CardContent>

                    <Box sx={{ p: { xs: 1.25, sm: 1.5 }, pt: 0 }}>
                      <Button
                        variant="contained"
                        fullWidth
                        onClick={(e) => {
                          e.stopPropagation();
                          handleProductClick(product.handle);
                        }}
                        sx={{
                          background: 'linear-gradient(to right, #214F7D, #3A8139)',
                          color: 'white',
                          borderRadius: '50px',
                          py: { xs: 0.75, sm: 1 },
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: { xs: '0.8rem', sm: '0.875rem' },
                          '&:hover': {
                            background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                          }
                        }}
                      >
                        Shop Now
                      </Button>
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        {!loading && !error && products.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No products available at the moment.
            </Typography>
          </Box>
        )}
      </Box>

      {/* contact section */}
      <Box className="contactSchoolSection" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 4 } }}>
        <Typography
          variant="h2"
          align="center"
          fontWeight="bold"
          className="subHeading"
          sx={{
            mt: { xs: 3, sm: 4, md: 5 },
            mb: 2,
          }}
        >
          Contact
        </Typography>
        <div className="d-flex-align-center-justify-center flex-direction-column">

          <p className="mb-0px" mt={0} style={{ fontSize: '0.9rem', textAlign: 'center', padding: '0 1rem', wordBreak: 'break-word' }}>
            <b>Email: </b>
            <Link href="mailto:gifts@i-am-peacekeeper.com" className='cursor-pointer'>gifts@i-am-peacekeeper.com </Link>
          </p>
        </div>
      </Box>

      {/* Login Modal with Side-by-Side Design */}
      <Modal
        open={openLoginOptionsModal}
        onClose={() => {
          setOpenLoginOptionsModal(false);
          // Reset OTP state when closing modal
          setOtpSent(false);
          setOtp('');
          setResendTimer(0);
          setShowPasswordField(false);
          setShowOtpField(false);
          setPeacekeeperPassword('');
        }}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500,
        }}
      >
        <Fade in={openLoginOptionsModal}>
          <Box sx={modalStyle}>
            {/* Header */}
            <Box sx={{ mb: { xs: 2, sm: 3 }, pb: { xs: 1.5, sm: 2 }, borderBottom: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 0.5, sm: 1 }, gap: 1 }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontFamily: 'var(--default-font)',
                  }}
                >
                  I Am Peacekeeper Gifts
                </Typography>
                <IconButton
                  onClick={() => setOpenLoginOptionsModal(false)}
                  sx={{
                    color: 'text.secondary',
                    padding: { xs: 0.5, sm: 1 },
                    '& svg': {
                      fontSize: { xs: '1.2rem', sm: '1.5rem' }
                    }
                  }}
                >
                  <Close />
                </IconButton>                
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  lineHeight: { xs: 1.4, sm: 1.5 }
                }}
              >
                You may browse other products directly within Shopify, no need to visit the Peacekeeper website for each individual product.
              </Typography>
            </Box>

            {/* Main Content - Side by Side */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 2, sm: 3 } }}>
              {/* Left Side - Login Options */}
              <Box sx={{
                width: { xs: '100%', sm: '35%', md: '40%' },
                pr: { xs: 0, sm: 2 },
                borderRight: { xs: 'none', sm: '1px solid #e0e0e0' },
                borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
                pb: { xs: 1, sm: 0 },
                mb: { xs: 1, sm: 0 }
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    mb: { xs: 2, sm: 3 },
                    fontWeight: 600,
                    color: '#214F7D',
                    fontSize: { xs: '1rem', sm: '1.25rem' }
                  }}
                >
                  Choose Login Method
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'center', sm: 'stretch' }, gap: { xs: 1.5, sm: 2 } }}>
                  <Button
                    variant={selectedLoginType === 'peacekeeper' ? 'contained' : 'outlined'}
                    onClick={() => {
                      setSelectedLoginType('peacekeeper');
                      // Reset to original state: only email field and two buttons
                      setShowPasswordField(false);
                      setShowOtpField(false);
                      setOtp('');
                      setOtpSent(false);
                      setResendTimer(0);
                      setPeacekeeperPassword('');
                    }}
                    sx={{
                      background: selectedLoginType === 'peacekeeper'
                        ? 'linear-gradient(to right, #214F7D, #3A8139)'
                        : 'transparent',
                      color: selectedLoginType === 'peacekeeper' ? 'white' : '#214F7D',
                      borderColor: '#214F7D',
                      borderRadius: '50px',
                      py: { xs: 1.25, sm: 1.5 },
                      px: { xs: 1.5, sm: 3 },
                      fontSize: { xs: '0.8rem', sm: '1rem' },
                      fontWeight: 500,
                      textTransform: 'none',
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      width: { xs: '80%', sm: 'auto' },
                      maxWidth: { xs: '80%', sm: 'none' },
                      '&:hover': {
                        background: selectedLoginType === 'peacekeeper'
                          ? 'linear-gradient(to right, #1a3f63, #2d6329)'
                          : 'rgba(33, 79, 125, 0.1)',
                      }
                    }}
                  >
                    I Am PeaceKeeper Login
                  </Button>

                  <Button
                    variant={selectedLoginType === 'referral' ? 'contained' : 'outlined'}
                    onClick={() => {
                      setSelectedLoginType('referral');
                      // Reset PeaceKeeper login state when switching
                      setShowPasswordField(false);
                      setShowOtpField(false);
                      setOtp('');
                      setOtpSent(false);
                      setResendTimer(0);
                      setPeacekeeperPassword('');
                    }}
                    sx={{
                      background: selectedLoginType === 'referral'
                        ? 'linear-gradient(to right, #214F7D, #3A8139)'
                        : 'transparent',
                      color: selectedLoginType === 'referral' ? 'white' : '#214F7D',
                      borderColor: '#214F7D',
                      borderRadius: '50px',
                      py: { xs: 1.25, sm: 1.5 },
                      px: { xs: 1.5, sm: 3 },
                      fontSize: { xs: '0.8rem', sm: '1rem' },
                      fontWeight: 500,
                      textTransform: 'none',
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      width: { xs: '80%', sm: 'auto' },
                      maxWidth: { xs: '80%', sm: 'none' },
                      '&:hover': {
                        background: selectedLoginType === 'referral'
                          ? 'linear-gradient(to right, #1a3f63, #2d6329)'
                          : 'rgba(33, 79, 125, 0.1)',
                      }
                    }}
                  >
                    Login with Referral ID
                  </Button>

                  <Button
                    variant="outlined"
                    onClick={() => {
                      handleExternalLink();
                      // Reset PeaceKeeper login state when switching
                      setShowPasswordField(false);
                      setShowOtpField(false);
                      setOtp('');
                      setOtpSent(false);
                      setResendTimer(0);
                      setPeacekeeperPassword('');
                    }}
                    sx={{
                      borderColor: '#214F7D',
                      color: '#214F7D',
                      borderRadius: '50px',
                      py: { xs: 1.25, sm: 1.5 },
                      px: { xs: 1.5, sm: 3 },
                      fontSize: { xs: '0.8rem', sm: '1rem' },
                      fontWeight: 500,
                      textTransform: 'none',
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      width: { xs: '80%', sm: 'auto' },
                      maxWidth: { xs: '80%', sm: 'none' },
                      '&:hover': {
                        background: 'rgba(33, 79, 125, 0.1)',
                        borderColor: '#1a3f63',
                      }
                    }}
                  >
                    Continue as Guest / Shopify Account
                  </Button>
                </Box>
              </Box>

              {/* Right Side - Login Form */}
              <Box sx={{
                width: { xs: '100%', sm: '65%', md: '60%' },
                pl: { xs: 0, sm: 3 },
                pt: { xs: 1, sm: 0 }
              }}>
                {selectedLoginType === 'peacekeeper' ? (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: { xs: 0.5, sm: 1 },
                        fontWeight: 600,
                        color: '#214F7D',
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                      }}
                    >
                      PeaceKeeper Login
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: { xs: 2, sm: 3 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Login for I Am Peacekeeper only
                    </Typography>

                    <Box component="form" onSubmit={(e) => {
                      e.preventDefault();
                      if (showPasswordField && peacekeeperPassword.trim()) {
                        handlePeacekeeperLogin(e);
                      }
                    }}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        value={peacekeeperId}
                        onChange={(e) => setPeacekeeperId(e.target.value)}
                        margin="normal"
                        required
                        placeholder="Enter your email"
                        sx={{
                          mb: { xs: 1.5, sm: 2 },
                          '& .MuiInputBase-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }
                        }}
                      />

                      {showPasswordField && (
                        <TextField
                          fullWidth
                          label="Password"
                          type={showPeacekeeperPassword ? 'text' : 'password'}
                          value={peacekeeperPassword}
                          onChange={(e) => setPeacekeeperPassword(e.target.value)}
                          margin="normal"
                          required
                          placeholder="Enter your password"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  onClick={() => setShowPeacekeeperPassword(!showPeacekeeperPassword)}
                                  edge="end"
                                  sx={{ padding: { xs: 0.5, sm: 1 } }}
                                >
                                  {showPeacekeeperPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            mb: { xs: 1.5, sm: 2 },
                            '& .MuiInputBase-root': {
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            }
                          }}
                        />
                      )}

                      {showOtpField && (
                        <TextField
                          fullWidth
                          label="OTP"
                          type="text"
                          value={otp}
                          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                          margin="normal"
                          required
                          placeholder="Enter OTP"
                          inputProps={{
                            maxLength: 6,
                          }}
                          sx={{
                            '& .MuiInputBase-input': {
                              textAlign: 'center',
                              fontSize: { xs: '1rem', sm: '1.2rem' },
                              letterSpacing: { xs: '0.2rem', sm: '0.3rem' },
                              fontWeight: '500'
                            },
                            '& .MuiInputBase-root': {
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            },
                            '& .MuiInputLabel-root': {
                              fontSize: { xs: '0.875rem', sm: '1rem' }
                            }
                          }}
                        />
                      )}

                      <Typography
                        variant='body2'
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          mb: { xs: 1.5, sm: 2 }
                        }}
                      >
                        Your OTP is valid for 7 minutes
                      </Typography>

                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: { xs: 0.75, sm: 1 } }}>
                        {/* LOGIN Button - Always visible */}
                        <Button
                          type={showPasswordField ? "submit" : "button"}
                          variant="contained"
                          size="small"
                          disabled={
                            isLoading ||
                            !peacekeeperId.trim() ||
                            (showPasswordField && !peacekeeperPassword.trim()) ||
                            (showOtpField && !otp.trim())
                          }
                          onClick={async () => {
                            if (showOtpField && otp.trim()) {
                              // If OTP field is shown and OTP is entered, verify OTP
                              await handleVerifyOtp();
                            } else if (showPasswordField && peacekeeperPassword.trim()) {
                              // If password field is shown, form will submit via type="submit"
                              // This onClick won't fire, but keeping for consistency
                            } else if (!showPasswordField && !showOtpField) {
                              // If no field is shown, show password field
                              setShowPasswordField(true);
                              setShowOtpField(false);
                              setOtp(''); // Reset OTP
                            } else if (showOtpField && !otp.trim()) {
                              // If OTP field is shown but no OTP, switch to password
                              setShowPasswordField(true);
                              setShowOtpField(false);
                              setOtp('');
                            } else if (showPasswordField) {
                              // If password field is shown, form will submit
                              // This is handled by form onSubmit
                            }
                          }}
                          sx={{
                            background: 'linear-gradient(to right, #214F7D, #3A8139)',
                            color: 'white',
                            borderRadius: '50px',
                            py: { xs: 0.875, sm: 1 },
                            px: { xs: 3, sm: 4 },
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            fontWeight: 500,
                            textTransform: 'none',
                            width: { xs: '80%', sm: 'auto' },
                            minWidth: { xs: '80%', sm: '150px' },
                            '&:hover': {
                              background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                            }
                          }}
                        >
                          {isLoading ? 'Logging in...' : (showOtpField ? 'LOGIN' : 'LOGIN')}
                        </Button>

                        {/* OR Separator - Always visible */}
                        <Typography
                          variant="body2"
                          sx={{
                            textAlign: 'center',
                            my: { xs: 0.25, sm: 0.5 },
                            color: 'text.secondary',
                            fontWeight: 500,
                            fontSize: { xs: '0.75rem', sm: '0.875rem' }
                          }}
                        >
                          OR
                        </Typography>

                        {/* LOGIN WITH OTP Button - Always visible */}
                        <Button
                          type="button"
                          variant="contained"
                          size="small"
                          disabled={otpLoading || !peacekeeperId.trim()}
                          onClick={async () => {
                            if (!showOtpField) {
                              // Show OTP field and send OTP
                              setShowOtpField(true);
                              setShowPasswordField(false);
                              setOtp(''); // Reset OTP when switching
                              await handleSendOtp();
                            } else if (otpSent) {
                              // If OTP is already sent, resend it
                              await handleResendOtp();
                            }
                          }}
                          startIcon={otpLoading ? <CircularProgress size={16} color="inherit" /> : null}
                          sx={{
                            background: 'linear-gradient(to right, #214F7D, #3A8139)',
                            color: 'white',
                            borderRadius: '50px',
                            py: { xs: 0.875, sm: 1 },
                            px: { xs: 3, sm: 4 },
                            fontSize: { xs: '0.8rem', sm: '0.875rem' },
                            fontWeight: 500,
                            textTransform: 'none',
                            width: { xs: '80%', sm: 'auto' },
                            minWidth: { xs: '80%', sm: '150px' },
                            '&:hover': {
                              background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                            }
                          }}
                        >
                          {otpLoading
                            ? 'Sending OTP...'
                            : (showOtpField && otpSent
                              ? (resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend OTP')
                              : 'LOGIN WITH OTP')
                          }
                        </Button>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="h6"
                      sx={{
                        mb: { xs: 0.5, sm: 1 },
                        fontWeight: 600,
                        color: '#214F7D',
                        fontSize: { xs: '1rem', sm: '1.25rem' }
                      }}
                    >
                      Login with Referral ID
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: { xs: 2, sm: 3 },
                        fontSize: { xs: '0.75rem', sm: '0.875rem' }
                      }}
                    >
                      Enter your referral id to login
                    </Typography>

                    <Box component="form" onSubmit={handleReferralLogin}>
                      <TextField
                        fullWidth
                        label="Referral ID"
                        type="text"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                        margin="normal"
                        required
                        placeholder="Enter your referral id"
                        sx={{
                          mb: { xs: 2, sm: 3 },
                          '& .MuiInputBase-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem' }
                          }
                        }}
                        inputProps={{
                          style: { textTransform: 'uppercase' }
                        }}
                      />

                      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Button
                          type="submit"
                          variant="contained"
                          disabled={isLoading}
                          sx={{
                            background: 'linear-gradient(to right, #214F7D, #3A8139)',
                            color: 'white',
                            borderRadius: '50px',
                            py: { xs: 1.25, sm: 1.5 },
                            px: { xs: 3, sm: 4 },
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            fontWeight: 500,
                            width: { xs: '100%', sm: 'auto' },
                            minWidth: { xs: '100%', sm: '150px' },
                            '&:hover': {
                              background: 'linear-gradient(to right, #1a3f63, #2d6329)',
                            }
                          }}
                        >
                          {isLoading ? 'Logging in...' : 'Login'}
                        </Button>
                      </Box>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Fade>
      </Modal>
    </Box>
  )
}

export default Gifts